import { useEffect, useRef } from "react";

// Animated geometric pattern inspired by the triangular A logo.
// Renders softly drifting gold lines/triangles on a dark navy background.
export default function HeroBackdrop() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let width = canvas.offsetWidth;
    let height = canvas.offsetHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    function resize() {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    window.addEventListener("resize", resize);

    const triangles = Array.from({ length: 14 }).map(() => ({
      x: Math.random() * width,
      y: Math.random() * height,
      r: 60 + Math.random() * 220,
      v: 0.05 + Math.random() * 0.1,
      a: 0.06 + Math.random() * 0.12,
      drift: (Math.random() - 0.5) * 0.18,
    }));

    function drawTriangle(t) {
      const x = t.x;
      const y = t.y;
      const r = t.r;
      ctx.beginPath();
      ctx.moveTo(x, y - r);
      ctx.lineTo(x - r * 0.86, y + r * 0.5);
      ctx.lineTo(x + r * 0.86, y + r * 0.5);
      ctx.closePath();
      ctx.strokeStyle = `rgba(212,175,55,${t.a})`;
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    function tick() {
      ctx.clearRect(0, 0, width, height);
      // background subtle gradient already on element; canvas only draws lines
      triangles.forEach((t) => {
        drawTriangle(t);
        // inner triangle for the layered A vibe
        drawTriangle({ ...t, r: t.r * 0.6, a: t.a * 0.8 });
        t.y -= t.v;
        t.x += t.drift;
        if (t.y + t.r < 0) {
          t.y = height + t.r;
          t.x = Math.random() * width;
        }
        if (t.x < -t.r) t.x = width + t.r;
        if (t.x > width + t.r) t.x = -t.r;
      });
      raf = requestAnimationFrame(tick);
    }
    tick();
    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute inset-0 bg-gradient-to-br from-[#000B24] via-[#001B54] to-[#000B24]" />
      <div
        className="absolute inset-0 opacity-50 mix-blend-screen"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 20% 0%, rgba(212,175,55,0.18), transparent 60%), radial-gradient(ellipse at 100% 60%, rgba(212,175,55,0.1), transparent 60%)",
        }}
      />
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>\")",
      }} />
    </div>
  );
}
